import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { theme } from 'antd';
import { RootState } from 'store/configureStore';
import { GroupOrganizationData } from 'store/ducks/Reporting/types';
import CustomTable from 'components/Tables/CustomTable';
import ApiService from 'utils/ApiCalls';
import dayjs from 'dayjs';
import * as Styled from 'components/Layouts/Layout.styled';
import * as LocalStyled from './GroupOrganization.styled';
import * as Constants from './GroupOrganization.constants';
import * as ReportingStyled from '../Reporting.styled';

const { useToken } = theme;

const GroupOrganization: React.FC = () => {
  const { token } = useToken();
  const [dataSource, setDataSource] = useState<GroupOrganizationData[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(null);
  const [shouldFetch, setShouldFetch] = useState(false);

  const { scheme } = useSelector((state: RootState) => ({
    scheme: state.global.scheme
  }));

  useEffect(() => {
    if (!shouldFetch) return;

    const fetchGroupOrganization = async () => {
      setLoading(true);
      try {
        const params: string[] = [];
        if (selectedDate)
          params.push(`date=${selectedDate.format('YYYY-MM-DD')}`);

        const response = await ApiService.getGroupOrganization(
          params.length > 0 ? params.join('&') : undefined
        );
        const apiData = response.data?.data;
        setDataSource(Array.isArray(apiData) ? apiData : []);
      } catch {
        setDataSource([]);
      } finally {
        setLoading(false);
        setShouldFetch(false);
      }
    };

    fetchGroupOrganization();
  }, [selectedDate, shouldFetch]);

  const handleDisplay = () => {
    setShouldFetch(true);
  };

  return (
    <>
      <LocalStyled.GlobalDatePickerStyle token={token} />
      <ReportingStyled.ReportTableWrapper>
        <Styled.StyledHorizontalFilterBar token={token} scheme={scheme}>
          <Styled.FilteredContainerRisks>
            <LocalStyled.StyledDatePicker
              placeholder='Date'
              value={selectedDate}
              onChange={(date) => setSelectedDate(date as dayjs.Dayjs | null)}
              popupClassName='white-bg-datepicker'
            />
            <Styled.Spacer />
            <Styled.StyledButton
              type='primary'
              onClick={handleDisplay}
              className='display-save-button'
            >
              Display
            </Styled.StyledButton>
          </Styled.FilteredContainerRisks>
        </Styled.StyledHorizontalFilterBar>

        <LocalStyled.GroupOrganizationTableWrapper>
          <CustomTable
            drawTable
            itemsCount={dataSource.length}
            columns={Constants.groupOrganizationColumns}
            dataSource={dataSource}
            loading={loading}
            rowKey={(record: GroupOrganizationData) =>
              `${record.group_id}-${record.division_id}-${record.region_id}-${record.legal_entity_id}-${record.business_unit_id}`
            }
            scroll={{ x: 2000, y: 420 }}
            sticky
            scheme={scheme}
          />
        </LocalStyled.GroupOrganizationTableWrapper>
      </ReportingStyled.ReportTableWrapper>
    </>
  );
};

export default GroupOrganization;
